import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import { authenticatedFetch } from '../api/apiClient'

/**
 * Fetches chart data for the specified date range
 * @param {string} startDate - ISO string for start date
 * @param {string} endDate - ISO string for end date
 * @param {string} [campaignFilter] - Optional campaign name to filter by
 * @returns {Promise<Object>} Chart data including various metrics
 */
export async function fetchChartData (startDate, endDate, campaignFilter) {
  try {
    logger.debug('Fetching chart data', { startDate, endDate, campaignFilter })

    let url = `${apiEndpoints.chartData}?start=${startDate}&end=${endDate}`

    if (campaignFilter) {
      url += `&campaign=${encodeURIComponent(campaignFilter)}`
    }

    const response = await authenticatedFetch(url)

    if (!response.ok) {
      const error = new Error(`Error fetching chart data: ${response.statusText}`)
      error.status = response.status
      throw error
    }

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json()
      logger.debug('Chart data received successfully')

      return {
        callsPerDay: Array.isArray(data.callsPerDay) ? data.callsPerDay : [],
        handleTimePerDay: Array.isArray(data.handleTimePerDay) ? data.handleTimePerDay : [],
        cssPerDay: Array.isArray(data.cssPerDay) ? data.cssPerDay : [],
        dispositionBreakdown: Array.isArray(data.dispositionBreakdown)
          ? data.dispositionBreakdown
          : [],
        callDirection: Array.isArray(data.callDirection) ? data.callDirection : []
      }
    } else {
      const text = await response.text()
      throw new Error(`Expected JSON but received: ${text}`)
    }
  } catch (error) {
    logger.error('Error fetching chart data', error)
    return {
      callsPerDay: [],
      handleTimePerDay: [],
      cssPerDay: [],
      dispositionBreakdown: [],
      callDirection: []
    }
  }
}

/**
 * Fetches metrics for the specified date range
 * @param {string} startDate - ISO string for start date
 * @param {string} endDate - ISO string for end date
 * @param {string} [campaignFilter] - Optional campaign name to filter by
 * @returns {Promise<Object>} Metrics data
 */
export async function fetchMetrics (startDate, endDate, campaignFilter) {
  try {
    logger.debug('Fetching metrics', { startDate, endDate, campaignFilter })

    let url = `${apiEndpoints.metrics}?start=${startDate}&end=${endDate}`

    if (campaignFilter) {
      url += `&campaign=${encodeURIComponent(campaignFilter)}`
    }
    logger.debug(`FETCHING VALUES USING URL: ${url}`)
    const response = await authenticatedFetch(url)

    if (!response.ok) {
      const error = new Error(`Error fetching metrics: ${response.statusText}`)
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('Metrics received successfully')

    return data
  } catch (error) {
    logger.error('Error fetching metrics', error)
    return null
  }
}

/**
 * Fetches reports for the specified date range and company
 *
 * NOTE: The Dashboard component limits this function to only fetch data from the last 24 hours
 * to improve performance, regardless of the date range selected by the user. Historical data
 * beyond 24 hours is populated by the backend backfill script that runs during application startup.
 *
 * @param {Date} startDate - Start date (will be limited to last 24 hours in the Dashboard component)
 * @param {Date} endDate - End date
 * @param {string} company - Company name
 * @param {string} [campaignFilter] - Optional campaign name to filter by
 * @returns {Promise<Object>} Report data
 */
/**
 * Fetches plan size for the specified company
 * @param {string} company - Company name/ID
 * @returns {Promise<Object>} Plan size data
 */
export async function fetchPlanSize (company) {
  try {
    logger.debug('Fetching plan size', { company })

    const response = await authenticatedFetch(`${apiEndpoints.planSize}?companyName=${encodeURIComponent(company)}&companyId=${encodeURIComponent(company)}`)

    if (!response.ok) {
      const error = new Error('Error fetching plan size')
      error.status = response.status
      throw error
    }

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json()
      logger.debug('Plan size received successfully')
      return data
    } else {
      const text = await response.text()
      throw new Error(`Expected JSON but received: ${text}`)
    }
  } catch (error) {
    logger.error('Error fetching plan size', error)
    return { planSize: null }
  }
}

/**
 * Fetches vote for a specific call
 * @param {string} callId - Call ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Vote data
 */
export async function fetchVote (callId, userId) {
  try {
    logger.debug('Fetching vote', { callId, userId })

    const response = await authenticatedFetch(`${apiEndpoints.votes}/${callId}/vote?user=${userId}`)

    if (!response.ok) {
      const error = new Error(`Error fetching vote: ${response.statusText}`)
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('Vote received successfully')
    return data
  } catch (error) {
    logger.error('Error fetching vote', error)
    return { vote: null }
  }
}

/**
 * Upvotes a specific call
 * @param {string} callId - Call ID
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Success status
 */
export async function upvoteCall (callId, userId) {
  try {
    logger.debug('Upvoting call', { callId, userId })

    const response = await authenticatedFetch(`${apiEndpoints.votes}/${callId}/upvote?user=${userId}`, {
      method: 'PUT'
    })

    if (response.ok) {
      logger.debug('Call upvoted successfully')
    } else {
      logger.warn('Failed to upvote call', {
        status: response.status,
        statusText: response.statusText
      })
    }

    return response.ok
  } catch (error) {
    logger.error('Error upvoting call', error)
    return false
  }
}

/**
 * Downvotes a specific call
 * @param {string} callId - Call ID
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} Success status
 */
export async function downvoteCall (callId, userId) {
  try {
    logger.debug('Downvoting call', { callId, userId })

    const response = await authenticatedFetch(`${apiEndpoints.votes}/${callId}/downvote?user=${userId}`, {
      method: 'PUT'
    })

    if (response.ok) {
      logger.debug('Call downvoted successfully')
    } else {
      logger.warn('Failed to downvote call', {
        status: response.status,
        statusText: response.statusText
      })
    }

    return response.ok
  } catch (error) {
    logger.error('Error downvoting call', error)
    return false
  }
}

/**
 * Fetches comments for a specific call
 * @param {string} callId - Call ID
 * @returns {Promise<Array>} Comments data
 */
export async function fetchComments (callId) {
  try {
    logger.debug('Fetching comments', { callId })

    const response = await authenticatedFetch(`${apiEndpoints.comments}/${callId}`)

    if (!response.ok) {
      const error = new Error('Failed to fetch comments')
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('Comments received successfully')
    return data
  } catch (error) {
    logger.error('Error fetching comments', error)
    return []
  }
}

/**
 * Adds a comment to a specific call
 * @param {string} callId - Call ID
 * @param {string} userId - User ID
 * @param {string} commentText - Comment text
 * @returns {Promise<Object>} New comment data
 */
export async function addComment (callId, userId, commentText) {
  try {
    logger.debug('Adding comment', { callId, userId })

    const response = await authenticatedFetch(apiEndpoints.comments, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ call_id: callId, user_id: userId, comment_text: commentText })
    })

    if (!response.ok) {
      const error = new Error('Failed to add comment')
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('Comment added successfully')
    return data
  } catch (error) {
    logger.error('Error adding comment', error)
    throw error
  }
}

/**
 * Updates a comment
 * @param {string} commentId - Comment ID
 * @param {string} commentText - Updated comment text
 * @returns {Promise<Object>} Updated comment data
 */
export async function updateComment (commentId, commentText) {
  try {
    logger.debug('Updating comment', { commentId })

    const response = await authenticatedFetch(`${apiEndpoints.comments}/${commentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment_text: commentText })
    })

    if (!response.ok) {
      const error = new Error('Failed to update comment')
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('Comment updated successfully')
    return data
  } catch (error) {
    logger.error('Error updating comment', error)
    throw error
  }
}

/**
 * Deletes a comment
 * @param {string} commentId - Comment ID
 * @returns {Promise<Object>} Response data
 */
export async function deleteComment (commentId) {
  try {
    logger.debug('Deleting comment', { commentId })

    const response = await authenticatedFetch(`${apiEndpoints.comments}/${commentId}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      const error = new Error('Failed to delete comment')
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('Comment deleted successfully')
    return data
  } catch (error) {
    logger.error('Error deleting comment', error)
    throw error
  }
}

/**
 * Processes XML reports data using the backend service
 * @param {string} clientUsageReportData - Client usage report XML data
 * @param {string} monthlyReportData - Monthly report XML data
 * @returns {Promise<Object>} Processed report data
 */
export async function processReports (clientUsageReportData, monthlyReportData) {
  try {
    logger.debug('Processing XML reports data', {
      hasClientData: !!clientUsageReportData,
      hasMonthlyData: !!monthlyReportData,
      clientDataLength: clientUsageReportData ? clientUsageReportData.length : 0,
      monthlyDataLength: monthlyReportData ? monthlyReportData.length : 0
    })

    if ((clientUsageReportData === '' || !clientUsageReportData) &&
        (monthlyReportData === '' || !monthlyReportData)) {
      logger.warn('No report data to process - returning empty result')
      return {
        rowsList: [],
        dispositionMap: {},
        timeString: 0
      }
    }

    logger.debug('Calling process-reports API', { endpoint: apiEndpoints.processReports })

    const response = await authenticatedFetch(apiEndpoints.processReports, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientUsageReportData,
        monthlyReportData
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      logger.error('Error processing reports', { status: response.status, response: errorText })
      const error = new Error(`Error processing reports: ${response.statusText}`)
      error.status = response.status
      throw error
    }

    const data = await response.json()
    logger.debug('XML reports processed successfully', {
      hasClientReport: !!data.clientReport,
      hasMonthlyReport: !!data.monthlyReport,
      rowsCount: data.clientReport?.rowsList?.length || 0,
      dispositionsCount: data.clientReport?.dispositionMap ? Object.keys(data.clientReport.dispositionMap).length : 0,
      timeString: data.monthlyReport?.timeString || 0
    })

    return {
      rowsList: data.clientReport?.rowsList || [],
      dispositionMap: data.clientReport?.dispositionMap || {},
      timeString: data.monthlyReport?.timeString || 0
    }
  } catch (error) {
    logger.error('Error processing XML reports', error)
    return {
      rowsList: [],
      dispositionMap: {},
      timeString: 0
    }
  }
}
